import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

export type Country = "ES" | "IT" | "FR";

export const COUNTRIES: Country[] = ["ES", "IT", "FR"];

export const COUNTRY_INFO: Record<
  Country,
  { flag: string; label: string; markets: string[] }
> = {
  ES: { flag: "🇪🇸", label: "España", markets: ["es", "axa_wl_es", "sql_es"] },
  IT: { flag: "🇮🇹", label: "Italia", markets: ["it"] },
  FR: { flag: "🇫🇷", label: "Francia", markets: ["fr"] },
};

const STORAGE_KEY = "admin:countries";
const DEFAULT_COUNTRIES: Country[] = ["ES"];

type MarketContextValue = {
  countries: Country[];
  toggleCountry: (country: Country) => void;
  markets: string[];
};

const MarketContext = createContext<MarketContextValue | null>(null);

function readStored(): Country[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_COUNTRIES;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return DEFAULT_COUNTRIES;
    const valid = parsed.filter((c): c is Country =>
      COUNTRIES.includes(c as Country),
    );
    return valid.length > 0 ? valid : DEFAULT_COUNTRIES;
  } catch {
    return DEFAULT_COUNTRIES;
  }
}

export function MarketProvider({ children }: { children: ReactNode }) {
  const [countries, setCountries] = useState<Country[]>(readStored);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(countries));
  }, [countries]);

  const toggleCountry = useCallback((country: Country) => {
    setCountries((prev) => {
      if (prev.includes(country)) {
        // Deselecting the last active country wraps around to "all selected",
        // mirroring how ops mentally think of it ("no filter = todo el mundo")
        // and avoiding the empty-state dead end.
        const next = prev.filter((c) => c !== country);
        return next.length === 0 ? COUNTRIES : next;
      }
      return [...prev, country];
    });
  }, []);

  const markets = useMemo(
    () => countries.flatMap((c) => COUNTRY_INFO[c].markets),
    [countries],
  );

  const value = useMemo(
    () => ({ countries, toggleCountry, markets }),
    [countries, toggleCountry, markets],
  );

  return (
    <MarketContext.Provider value={value}>{children}</MarketContext.Provider>
  );
}

export function useMarket(): MarketContextValue {
  const ctx = useContext(MarketContext);
  if (!ctx) {
    throw new Error("useMarket must be used inside MarketProvider");
  }
  return ctx;
}
